import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import axios from 'axios'
import { useUserInfo } from '@/store/userStore'
import { useSettings } from '@/store/settingStore'
import { ThemeMode } from '#/enum'
import { FiBarChart2, FiDatabase, FiArrowRight, FiInfo } from 'react-icons/fi'
import { LuSparkles } from 'react-icons/lu'

type AgentKey = 'merging' | 'cleaning' | 'dashboard'

interface WorkflowStepItem {
  step: number
  title: string
  desc: string
}

interface AgentWorkflowData {
  id: 'overview' | 'merging' | 'cleaning' | 'dashboard'
  title: string
  badge: string
  subtitle: string
  steps: WorkflowStepItem[]
}

const AGENT_WORKFLOWS: Record<string, AgentWorkflowData> = {
  overview: {
    id: 'overview',
    title: 'How it works',
    badge: 'Platform Overview',
    subtitle: 'From raw files to actionable insights in four streamlined steps.',
    steps: [
      {
        step: 1,
        title: 'Upload Data',
        desc: 'Upload your CSV or Excel files securely.',
      },
      {
        step: 2,
        title: 'Run AI Agents',
        desc: 'Clean and merge your data using specialized agents.',
      },
      {
        step: 3,
        title: 'Get Insights',
        desc: 'Ask questions and generate dashboards with VantageAI.',
      },
      {
        step: 4,
        title: 'Make Decisions',
        desc: 'Use insights to make smarter, data-driven decisions.',
      },
    ],
  },
  merging: {
    id: 'merging',
    title: 'How Data Merging Works',
    badge: 'Data Merging Flow',
    subtitle: 'Combine multiple datasets with automated key detection and conflict resolution.',
    steps: [
      {
        step: 1,
        title: 'Upload Datasets',
        desc: 'Upload two or more CSV or Excel files you want to combine.',
      },
      {
        step: 2,
        title: 'Select Join Keys',
        desc: 'Identify matching key columns and choose your join strategy.',
      },
      {
        step: 3,
        title: 'AI Schema Match',
        desc: 'AI automatically resolves column conflicts, duplicates, and type mismatches.',
      },
      {
        step: 4,
        title: 'Unified Dataset',
        desc: 'Export consolidated data or push directly into dashboard analytics.',
      },
    ],
  },
  cleaning: {
    id: 'cleaning',
    title: 'How Data Cleaning Works',
    badge: 'Data Cleaning Flow',
    subtitle: 'Automate data quality audits, anomaly remediation, and formatting in seconds.',
    steps: [
      {
        step: 1,
        title: 'Upload Raw Data',
        desc: 'Upload your dataset containing missing values or format discrepancies.',
      },
      {
        step: 2,
        title: 'AI Quality Audit',
        desc: 'AI inspects data types, scans for nulls, duplicates, and outliers.',
      },
      {
        step: 3,
        title: 'Smart Cleaning',
        desc: 'Apply 1-click rules to impute data, normalize headers, and fix syntax.',
      },
      {
        step: 4,
        title: 'Pristine Export',
        desc: 'Download clean sanitized data or send directly to the dashboard engine.',
      },
    ],
  },
  dashboard: {
    id: 'dashboard',
    title: 'How Smart Dashboard Works',
    badge: 'Smart Dashboard Flow',
    subtitle: 'Conversational analytics that transforms business questions into interactive live dashboards.',
    steps: [
      {
        step: 1,
        title: 'Connect Dataset',
        desc: 'Upload your spreadsheet or select an existing cleaned dataset.',
      },
      {
        step: 2,
        title: 'Domain & Context',
        desc: 'AI detects business domain, key metrics, and surfaces smart prompts.',
      },
      {
        step: 3,
        title: 'Auto-Generate Visuals',
        desc: 'AI plans and generates tailored interactive charts and KPI widgets.',
      },
      {
        step: 4,
        title: 'Interactive Dashboard',
        desc: 'Explore KPIs, filter data in real-time, and export presentation-ready reports.',
      },
    ],
  },
}

const NewVantageAIHomePage = () => {
  const { name } = useUserInfo()
  const { themeMode } = useSettings()
  const navigate = useNavigate()
  const [dashboardCount, setDashboardCount] = useState<number | null>(null)
  const [hoveredAgent, setHoveredAgent] = useState<AgentKey | null>(null)

  const userId = localStorage.getItem('userId')
  const roleId = localStorage.getItem('roleId')
  const isRestrictedRole = ["1", "4"].includes(String(roleId))
  const { VITE_APP_URL: URL } = import.meta.env
  const isDark = themeMode === ThemeMode.Dark
  const activeWorkflow = hoveredAgent ? AGENT_WORKFLOWS[hoveredAgent] : AGENT_WORKFLOWS.overview

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour >= 5 && hour < 12) {
      return 'Good Morning'
    } else if (hour >= 12 && hour < 17) {
      return 'Good Afternoon'
    } else if (hour >= 17 && hour < 22) {
      return 'Good Evening'
    } else {
      return 'Good Night'
    }
  }
  const greeting = getGreeting()

  // useEffect(() => {
  //   const fetchStats = async () => {
  //     if (!userId) return
  //     try {
  //       const response = await axios.get(`${URL}/api/dashboards/all-dashboards`, {
  //         headers: {
  //           'User-Id': userId,
  //         },
  //       })
  //       const list = response.data.dashboards || response.data.active_dashboards || []
  //       setDashboardCount(list.length)
  //     } catch (error) {
  //       console.error('Error fetching dashboards for home stats:', error)
  //     }
  //   }
  //   fetchStats()
  // }, [userId, URL])

  return (
    <div className={`w-full p-4 flex flex-col gap-8 mx-auto transition-colors duration-300 ${isDark ? 'text-white' : 'text-slate-800'}`}>
      {/* Scoped CSS Styles for Micro-animations */}
      <style>{`
        @keyframes waveHand {
          0% { transform: rotate(0deg); }
          10% { transform: rotate(14deg); }
          20% { transform: rotate(-8deg); }
          30% { transform: rotate(14deg); }
          40% { transform: rotate(-4deg); }
          50% { transform: rotate(10deg); }
          60% { transform: rotate(0deg); }
          100% { transform: rotate(0deg); }
        }
        .animate-hand-wave {
          animation: waveHand 2.5s infinite;
          transform-origin: 70% 70%;
          display: inline-block;
        }
        @keyframes pulseDot {
          0%, 100% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(1.8); opacity: 0; }
        }
        .animate-pulse-dot {
          animation: pulseDot 2s infinite ease-out;
        }
        @keyframes stepFadeIn {
          0% { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-step-fade {
          animation: stepFadeIn 0.3s ease-out forwards;
        }
      `}</style>

      {/* Hero Section Container */}
      <div
        className={`relative overflow-hidden w-full p-8 border rounded-2xl flex flex-col lg:flex-row justify-between items-center gap-8 transition-all duration-300 ${isDark
          ? 'border-red-950/30 shadow-[0_4px_24px_rgba(0,0,0,0.4)]'
          : 'border-red-100/50 shadow-[0_4px_24px_rgba(196,18,48,0.02)]'
          }`}
        style={{
          background: isDark
            ? 'linear-gradient(108deg, #0b111e 0%, #070a12 50%, #0c1524 100%)'
            : 'linear-gradient(108deg, #FFF6F6 0%, #FFFFFF 50%, #FFF2F3 100%)'
        }}
      >
        {/* Soft Decorative Ambient Glows */}
        <div className={`absolute right-[-10%] top-[-20%] w-[50%] h-[140%] rounded-full bg-gradient-to-br to-transparent blur-3xl pointer-events-none transition-all duration-300 ${isDark ? 'from-red-950/20 opacity-30' : 'from-[#FFE4E6] opacity-50'
          }`} />
        <div className={`absolute left-[-10%] bottom-[-20%] w-[40%] h-[100%] rounded-full bg-gradient-to-tr to-transparent blur-2xl pointer-events-none transition-all duration-300 ${isDark ? 'from-slate-900/30 opacity-30' : 'from-[#FFF1F2] opacity-40'
          }`} />

        {/* Decorative Grid Dot Pattern */}
        <div className={`absolute inset-0 pointer-events-none bg-[radial-gradient(#C41230_1.5px,transparent_1.5px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_at_right,white,transparent_75%)] transition-all duration-300 ${isDark ? 'opacity-[0.05]' : 'opacity-[0.035]'
          }`} />

        {/* Left Content Column */}
        <div className="flex flex-col gap-6 z-10 w-full lg:w-1/2">
          <div className="flex flex-col">
            <span className={`text-xl font-semibold tracking-wide ${isDark ? 'text-slate-300' : 'text-black'}`}>{greeting},</span>
            <h1 className={`text-4xl lg:text-5xl font-black tracking-tight mt-1 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {name} <span className="animate-hand-wave">👋</span>
            </h1>

            <div className="flex flex-col mt-5">
              <h3 className={`text-lg font-bold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>Your AI-powered workspace is ready.</h3>
              <p className={`w-full max-w-[420px] mt-1.5 font-medium leading-relaxed ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Upload your data and let our AI agents clean, merge and analyze it to deliver powerful insights.
              </p>
            </div>
          </div>

          {/* Quick Metrics / Stats Cards */}
          <div className="flex flex-wrap gap-4 mt-2">
            {/* Stat Card 1 */}
            {/* <div className={`backdrop-blur-sm border rounded-xl p-3 flex items-center gap-3 hover:-translate-y-0.5 transition-all duration-300 min-w-[130px] flex-1 sm:flex-initial ${
              isDark 
                ? 'bg-[#0f172a]/60 border-slate-800/80 shadow-[0_2px_8px_rgba(0,0,0,0.3)] hover:shadow-[0_8px_16px_rgba(0,0,0,0.5)]' 
                : 'bg-white/80 border-red-50/50 shadow-[0_2px_8px_rgba(0,0,0,0.01)] hover:shadow-[0_8px_16px_rgba(0,0,0,0.03)]'
            }`}>
              <div className={`p-2 rounded-lg flex items-center justify-center shadow-inner ${
                isDark ? 'bg-[#C41230]/20 text-[#ff4b6b]' : 'bg-[#FFF1F2] text-[#C41230]'
              }`}>
                <FiBarChart2 className="w-5 h-5" />
              </div>
              <div className="flex flex-col">
                <span className={`text-xl font-extrabold leading-none ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  {dashboardCount !== null ? dashboardCount : 12}
                </span>
                <span className={`text-xs font-semibold mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Dashboards</span>
              </div>
            </div> */}

            {/* Stat Card 2 */}
            {/* <div className={`backdrop-blur-sm border rounded-xl p-3 flex items-center gap-3 hover:-translate-y-0.5 transition-all duration-300 min-w-[130px] flex-1 sm:flex-initial ${
              isDark 
                ? 'bg-[#0f172a]/60 border-slate-800/80 shadow-[0_2px_8px_rgba(0,0,0,0.3)] hover:shadow-[0_8px_16px_rgba(0,0,0,0.5)]' 
                : 'bg-white/80 border-red-50/50 shadow-[0_2px_8px_rgba(0,0,0,0.01)] hover:shadow-[0_8px_16px_rgba(0,0,0,0.03)]'
            }`}>
              <div className={`p-2 rounded-lg flex items-center justify-center shadow-inner ${
                isDark ? 'bg-[#10B981]/20 text-[#34d399]' : 'bg-[#E6FBF3] text-[#10B981]'
              }`}>
                <FiDatabase className="w-5 h-5" />
              </div>
              <div className="flex flex-col">
                <span className={`text-xl font-extrabold leading-none ${isDark ? 'text-white' : 'text-slate-800'}`}>8</span>
                <span className={`text-xs font-semibold mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Datasets</span>
              </div>
            </div> */}

            {/* Stat Card 3 */}
            {/* <div className={`backdrop-blur-sm border rounded-xl p-3 flex items-center gap-3 hover:-translate-y-0.5 transition-all duration-300 min-w-[130px] flex-1 sm:flex-initial ${
              isDark 
                ? 'bg-[#0f172a]/60 border-slate-800/80 shadow-[0_2px_8px_rgba(0,0,0,0.3)] hover:shadow-[0_8px_16px_rgba(0,0,0,0.5)]' 
                : 'bg-white/80 border-red-50/50 shadow-[0_2px_8px_rgba(0,0,0,0.01)] hover:shadow-[0_8px_16px_rgba(0,0,0,0.03)]'
            }`}>
              <div className={`p-2 rounded-lg flex items-center justify-center shadow-inner ${
                isDark ? 'bg-[#8B5CF6]/20 text-[#a78bfa]' : 'bg-[#F3E8FF] text-[#8B5CF6]'
              }`}>
                <LuSparkles className="w-5 h-5" />
              </div>
              <div className="flex flex-col">
                <span className={`text-xl font-extrabold leading-none ${isDark ? 'text-white' : 'text-slate-800'}`}>23</span>
                <span className={`text-xs font-semibold mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Insights Generated</span>
              </div>
            </div> */}
          </div>
        </div>

        {/* Right Illustration Column */}
        <div className="relative z-10 w-full lg:w-1/2 flex items-center justify-center lg:justify-end min-h-[260px]">
          {/* Main Dashboard Canvas Mockup */}
          <div className={`rounded-xl flex flex-col p-4 w-[380px] h-[230px] relative overflow-hidden select-none transition-all duration-300 ${isDark
            ? 'bg-[#0f172a] border border-slate-800/80 shadow-[0_12px_40px_rgba(0,0,0,0.4)] hover:shadow-[0_16px_48px_rgba(0,0,0,0.6)]'
            : 'bg-white border border-slate-100/80 shadow-[0_12px_40px_rgba(0,0,0,0.04)] hover:shadow-[0_16px_48px_rgba(0,0,0,0.06)]'
            }`}>
            {/* Header of Mockup */}
            <div className={`flex items-center justify-between border-b pb-2 mb-2 ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444]" />
                <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" />
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
              </div>
              <div className={`w-32 h-2.5 rounded-full ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`} />
              <div className={`w-4 h-4 rounded-full ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`} />
            </div>

            {/* Content of Mockup */}
            <div className="flex flex-1 gap-3 overflow-hidden">
              {/* Mockup Sidebar */}
              <div className={`w-[45px] border-r flex flex-col gap-2 pt-1.5 ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
                <div className={`w-full h-1.5 rounded ${isDark ? 'bg-red-950/40' : 'bg-red-100/60'}`} />
                <div className={`w-4/5 h-1.5 rounded ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`} />
                <div className={`w-3/4 h-1.5 rounded ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`} />
                <div className={`w-5/6 h-1.5 rounded ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`} />
              </div>

              {/* Mockup Dashboard Main Content */}
              <div className="flex-1 flex flex-col gap-2.5 pt-1.5">
                {/* Stats Row inside Mockup */}
                <div className="flex justify-between items-center">
                  <div className="flex flex-col gap-1">
                    <div className={`w-12 h-2.5 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`} />
                    <div className={`w-20 h-1.5 rounded ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`} />
                  </div>

                  {/* Circular Donut Progress inside Mockup */}
                  <div className={`relative w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 ${isDark ? 'bg-slate-800/40' : 'bg-slate-50'}`}>
                    <svg className="w-full h-full transform -rotate-90">
                      <circle cx="18" cy="18" r="14" stroke={isDark ? "#1e293b" : "#F1F5F9"} strokeWidth="2.5" fill="transparent" />
                      <circle
                        cx="18"
                        cy="18"
                        r="14"
                        stroke="#C41230"
                        strokeWidth="2.5"
                        fill="transparent"
                        strokeDasharray="88"
                        strokeDashoffset="65"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span className={`absolute text-[7px] font-extrabold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>18%</span>
                  </div>
                </div>

                {/* Flowing Line Wave Chart inside Mockup */}
                <div className={`flex-1 rounded-lg p-1 relative overflow-hidden flex items-end ${isDark ? 'bg-slate-900/40' : 'bg-slate-50/50'}`}>
                  <svg className="w-full h-14" viewBox="0 0 120 40" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="illustrationGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#C41230" stopOpacity={isDark ? "0.06" : "0.15"} />
                        <stop offset="100%" stopColor="#C41230" stopOpacity="0.0" />
                      </linearGradient>
                    </defs>
                    <path d="M0 35 Q 20 8, 40 28 T 80 15 T 120 6 L 120 40 L 0 40 Z" fill="url(#illustrationGradient)" />
                    <path d="M0 35 Q 20 8, 40 28 T 80 15 T 120 6" fill="none" stroke="#C41230" strokeWidth="1.5" strokeLinecap="round" />
                    {/* Glowing active point indicator */}
                    <circle cx="120" cy="6" r="2" fill="#C41230" />
                  </svg>
                </div>

                {/* Sub Bar row */}
                <div className="flex gap-2">
                  <div className={`w-1/3 h-1.5 rounded ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`} />
                  <div className={`w-1/4 h-1.5 rounded ${isDark ? 'bg-red-950/30' : 'bg-[#FFF1F2]'}`} />
                  <div className={`w-1/6 h-1.5 rounded ${isDark ? 'bg-emerald-950/30' : 'bg-[#E6FBF3]'}`} />
                </div>
              </div>
            </div>
          </div>

          {/* Overlapping "AI Insight" Bubble Card */}
          <div className={`absolute bottom-2 right-[2%] sm:right-[5%] md:right-[8%] lg:right-[2%] xl:right-[8%] border rounded-xl p-3 w-48 z-20 flex flex-col gap-1.5 hover:scale-[1.03] transition-all duration-300 cursor-pointer pointer-events-auto ${isDark
            ? 'bg-[#0f172a] border-slate-800 shadow-[0_12px_32px_rgba(0,0,0,0.6)]'
            : 'bg-white border-red-50 shadow-[0_12px_32px_rgba(196,18,48,0.08)]'
            }`}>
            <div className={`flex items-center gap-1.5 text-xs font-bold ${isDark ? 'text-red-400' : 'text-[#C41230]'}`}>
              <span className={`p-1 rounded-lg ${isDark ? 'bg-red-950/50' : 'bg-[#FFF1F2]'}`}>
                <LuSparkles className="w-3.5 h-3.5" />
              </span>
              AI Insight
            </div>
            <p className={`text-[10px] font-semibold leading-normal ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              Your data reveals an <span className={`font-bold ${isDark ? 'text-red-400' : 'text-[#C41230]'}`}>18% increase</span> in retention this quarter.
            </p>
          </div>
        </div>
      </div>

      {/* Your AI Agents Section */}
      <div className="flex flex-col gap-4 mt-2">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${isDark ? 'bg-red-950/50 text-red-400' : 'bg-[#FFF1F2] text-[#C41230]'}`}>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>
            </div>
            <div className="flex flex-col">
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Your AI Agents</h2>
              <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-400'}`}>Hover over any agent card to preview its workflow below.</span>
            </div>
          </div>
        </div>

        {/* AI Agents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Card 3: VantageAI (Smart Dashboard Agent) */}
          <div
            onMouseEnter={() => setHoveredAgent('dashboard')}
            onMouseLeave={() => setHoveredAgent(null)}
            className={`relative rounded-2xl p-6 shadow-sm order-3 transition-all duration-300 flex flex-row gap-5 items-stretch border ${isRestrictedRole ? 'cursor-default' : 'cursor-pointer'} ${
              isDark ? 'bg-red-950/10' : 'bg-[#FFF7F8]'
            } ${
              hoveredAgent === 'dashboard'
                ? isDark
                  ? 'border-red-500/80 -translate-y-1.5 shadow-[0_8px_28px_rgba(196,18,48,0.3)] ring-2 ring-red-500/40 bg-red-950/25'
                  : 'border-[#C41230] -translate-y-1.5 shadow-[0_8px_28px_rgba(196,18,48,0.14)] ring-2 ring-[#C41230]/20 bg-white'
                : hoveredAgent
                  ? isDark
                    ? 'border-red-900/20 opacity-70 hover:opacity-100'
                    : 'border-[#FFF1F2] opacity-70 hover:opacity-100'
                  : isDark
                    ? 'border-red-900/30 hover:-translate-y-1 hover:shadow-md'
                    : 'border-[#FFF1F2] hover:-translate-y-1 hover:shadow-md'
            }`}
          >
            {/* Active Hover Indicator Badge */}
            {hoveredAgent === 'dashboard' && (
              <div className={`absolute top-2.5 right-3 px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider flex items-center gap-1 ${
                isDark ? 'bg-red-950/80 text-red-300 border border-red-800/60' : 'bg-red-50 text-[#C41230] border border-red-200'
              }`}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#C41230] animate-pulse" />
                Active Preview
              </div>
            )}

            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${
              hoveredAgent === 'dashboard' ? 'scale-105' : ''
            } ${isDark ? 'bg-red-950/50 text-red-400' : 'bg-[#FFF1F2] text-[#C41230]'}`}>
              {/* Magic Sparkles Custom SVG */}
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 4c0 3.3-2.7 6-6 6 3.3 0 6 2.7 6 6 0-3.3 2.7-6 6-6-3.3 0-6-2.7-6-6z" />
                <path d="M19 6c0 1.5-1.2 2.7-2.7 2.7 1.5 0 2.7 1.2 2.7 2.7 0-1.5 1.2-2.7 2.7-2.7-1.5 0-2.7-1.2-2.7-2.7z" />
                <path d="M18 15c0 1-0.8 1.8-1.8 1.8 1 0 1.8 0.8 1.8 1.8 0-1 0.8-1.8 1.8-1.8-1 0-1.8-0.8-1.8-1.8z" />
              </svg>
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <h3 className={`font-bold text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>Smart Dashboard Agent</h3>
              <p className={`text-sm font-medium leading-relaxed ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Ask questions, generate insights and build dashboards using the power of AI.
              </p>
              <button
                disabled={isRestrictedRole}
                onClick={(e) => {
                  e.stopPropagation()
                  if (!isRestrictedRole) {
                    navigate('/agents/smart-dashboard-agent')
                  }
                }}
                className={`flex items-center justify-between w-full max-w-[190px] font-bold py-2.5 px-5 rounded-xl text-sm mt-auto transition-all duration-200 ${
                  isRestrictedRole
                    ? isDark ? 'bg-slate-700 text-slate-400 cursor-not-allowed opacity-60' : 'bg-gray-200 text-gray-500 cursor-not-allowed opacity-70'
                    : isDark
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-[#C41230] text-white shadow-[0_2px_10px_rgba(196,18,48,0.22)] hover:bg-[#a51028]'
                  }`}
              >
                <span>Smart Dashboard Agent</span>
                <FiArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Card 1: Data Cleaning */}
          <div
            onMouseEnter={() => setHoveredAgent('cleaning')}
            onMouseLeave={() => setHoveredAgent(null)}
            className={`relative rounded-2xl p-6 shadow-sm order-2 transition-all duration-300 flex flex-row gap-5 items-stretch border ${isRestrictedRole ? 'cursor-default' : 'cursor-pointer'} ${
              isDark ? 'bg-red-950/10' : 'bg-[#FFF7F8]'
            } ${
              hoveredAgent === 'cleaning'
                ? isDark
                  ? 'border-red-500/80 -translate-y-1.5 shadow-[0_8px_28px_rgba(196,18,48,0.3)] ring-2 ring-red-500/40 bg-red-950/25'
                  : 'border-[#C41230] -translate-y-1.5 shadow-[0_8px_28px_rgba(196,18,48,0.14)] ring-2 ring-[#C41230]/20 bg-white'
                : hoveredAgent
                  ? isDark
                    ? 'border-red-900/20 opacity-70 hover:opacity-100'
                    : 'border-[#FFF1F2] opacity-70 hover:opacity-100'
                  : isDark
                    ? 'border-red-900/30 hover:-translate-y-1 hover:shadow-md'
                    : 'border-[#FFF1F2] hover:-translate-y-1 hover:shadow-md'
            }`}
          >
            {/* Active Hover Indicator Badge */}
            {hoveredAgent === 'cleaning' && (
              <div className={`absolute top-2.5 right-3 px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider flex items-center gap-1 ${
                isDark ? 'bg-red-950/80 text-red-300 border border-red-800/60' : 'bg-red-50 text-[#C41230] border border-red-200'
              }`}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#C41230] animate-pulse" />
                Active Preview
              </div>
            )}

            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${
              hoveredAgent === 'cleaning' ? 'scale-105' : ''
            } ${isDark ? 'bg-red-950/50 text-red-400' : 'bg-[#FFF1F2] text-[#C41230]'}`}>
              {/* Broom / Cleaning Custom SVG */}
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                <path d="M4 18l6-6M9 9l3 3M19 4l-4 4M14 9l1 1M18 5c.5.5.5 1.5 0 2s-1.5.5-2 0-.5-1.5 0-2 1.5-.5 2 0z" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 20l1.5-1.5M3 18l1.5 1.5" strokeLinecap="round" />
                <circle cx="20" cy="11" r="1" fill="currentColor" />
                <circle cx="15" cy="15" r="1.5" fill="currentColor" />
              </svg>
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <h3 className={`font-bold text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>Data Cleaning</h3>
              <p className={`text-sm font-medium leading-relaxed ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Clean and standardize your data. Handle missing values, duplicates and inconsistencies automatically.
              </p>
              <button
                disabled={isRestrictedRole}
                onClick={(e) => {
                  e.stopPropagation()
                  if (!isRestrictedRole) {
                    navigate('/agents/data-cleaning')
                  }
                }}
                className={`flex items-center justify-between w-full max-w-[180px] font-bold py-2.5 px-5 rounded-xl text-sm mt-auto transition-all duration-200 ${
                  isRestrictedRole
                    ? isDark ? 'bg-slate-700 text-slate-400 cursor-not-allowed opacity-60' : 'bg-gray-200 text-gray-500 cursor-not-allowed opacity-70'
                    : isDark
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-[#C41230] text-white shadow-[0_2px_10px_rgba(196,18,48,0.22)] hover:bg-[#a51028]'
                  }`}
              >
                <span>Clean Data</span>
                <FiArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Card 2: Data Merging */}
          <div
            onMouseEnter={() => setHoveredAgent('merging')}
            onMouseLeave={() => setHoveredAgent(null)}
            className={`relative rounded-2xl p-6 shadow-sm order-1 transition-all duration-300 flex flex-row gap-5 items-stretch border ${isRestrictedRole ? 'cursor-default' : 'cursor-pointer'} ${
              isDark ? 'bg-red-950/10' : 'bg-[#FFF7F8]'
            } ${
              hoveredAgent === 'merging'
                ? isDark
                  ? 'border-red-500/80 -translate-y-1.5 shadow-[0_8px_28px_rgba(196,18,48,0.3)] ring-2 ring-red-500/40 bg-red-950/25'
                  : 'border-[#C41230] -translate-y-1.5 shadow-[0_8px_28px_rgba(196,18,48,0.14)] ring-2 ring-[#C41230]/20 bg-white'
                : hoveredAgent
                  ? isDark
                    ? 'border-red-900/20 opacity-70 hover:opacity-100'
                    : 'border-[#FFF1F2] opacity-70 hover:opacity-100'
                  : isDark
                    ? 'border-red-900/30 hover:-translate-y-1 hover:shadow-md'
                    : 'border-[#FFF1F2] hover:-translate-y-1 hover:shadow-md'
            }`}
          >
            {/* Active Hover Indicator Badge */}
            {hoveredAgent === 'merging' && (
              <div className={`absolute top-2.5 right-3 px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider flex items-center gap-1 ${
                isDark ? 'bg-red-950/80 text-red-300 border border-red-800/60' : 'bg-red-50 text-[#C41230] border border-red-200'
              }`}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#C41230] animate-pulse" />
                Active Preview
              </div>
            )}

            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${
              hoveredAgent === 'merging' ? 'scale-105' : ''
            } ${isDark ? 'bg-red-950/50 text-red-400' : 'bg-[#FFF1F2] text-[#C41230]'}`}>
              {/* Database Joining Custom SVG */}
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <ellipse cx="9" cy="6" rx="4" ry="2" />
                <path d="M5 6v8c0 1.1.9 2 4 2s4-.9 4-2V6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 10c0 1.1.9 2 4 2s4-.9 4-2" strokeLinecap="round" strokeLinejoin="round" />

                <ellipse cx="15" cy="10" rx="4" ry="2" fill={isDark ? "rgba(196, 18, 48, 0.5)" : "#FFF1F2"} />
                <ellipse cx="15" cy="10" rx="4" ry="2" />
                <path d="M11 10v8c0 1.1.9 2 4 2s4-.9 4-2v-8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M11 14c0 1.1.9 2 4 2s4-.9 4-2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <h3 className={`font-bold text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>Data Merging</h3>
              <p className={`text-sm font-medium leading-relaxed ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Merge multiple datasets with ease. Join, combine and unify your data for deeper analysis.
              </p>
              <button
                disabled={isRestrictedRole}
                onClick={(e) => {
                  e.stopPropagation()
                  if (!isRestrictedRole) {
                    navigate('/agents/merge-files')
                  }
                }}
                className={`flex items-center justify-between w-full max-w-[180px] font-bold py-2.5 px-5 rounded-xl text-sm mt-auto transition-all duration-200 ${
                  isRestrictedRole
                    ? isDark ? 'bg-slate-700 text-slate-400 cursor-not-allowed opacity-60' : 'bg-gray-200 text-gray-500 cursor-not-allowed opacity-70'
                    : isDark
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-[#C41230] text-white shadow-[0_2px_10px_rgba(196,18,48,0.22)] hover:bg-[#a51028]'
                  }`}
              >
                <span>Merge Data</span>
                <FiArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>


        </div>
      </div>

      {/* How it works Section */}
      <div className={`w-full border rounded-2xl p-6 lg:p-8 flex flex-col gap-6 transition-all duration-300 ${isDark
        ? 'bg-[#0b111e]/80 border-slate-800 shadow-[0_1px_3px_rgba(0,0,0,0.4)]'
        : 'bg-white border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.01)]'
        } ${hoveredAgent ? (isDark ? 'border-red-900/50 shadow-[0_4px_24px_rgba(196,18,48,0.12)]' : 'border-red-100 shadow-[0_4px_24px_rgba(196,18,48,0.05)]') : ''}`}>
        
        {/* Header with Title, Badge, and Subtitle */}
        <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-4 transition-colors duration-300 ${
          isDark ? 'border-slate-800/80' : 'border-slate-100'
        }`}>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className={`text-xl font-bold tracking-wide transition-colors duration-200 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                {activeWorkflow.title}
              </h2>
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold tracking-wide transition-all duration-300 ${
                  hoveredAgent
                    ? isDark
                      ? 'bg-red-950/70 text-red-300 border border-red-800/60 shadow-[0_0_12px_rgba(196,18,48,0.25)]'
                      : 'bg-red-50 text-[#C41230] border border-red-200 shadow-[0_0_12px_rgba(196,18,48,0.08)]'
                    : isDark
                      ? 'bg-slate-800/80 text-slate-300 border border-slate-700'
                      : 'bg-slate-100 text-slate-600 border border-slate-200'
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    hoveredAgent ? 'bg-[#C41230] animate-pulse' : isDark ? 'bg-slate-400' : 'bg-slate-400'
                  }`}
                />
                {activeWorkflow.badge}
              </span>
            </div>
            <p className={`text-xs sm:text-sm font-medium transition-colors duration-200 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              {activeWorkflow.subtitle}
            </p>
          </div>

          {hoveredAgent && (
            <span className={`text-[11px] font-semibold flex items-center gap-1 self-start sm:self-auto transition-all duration-200 ${
              isDark ? 'text-red-400/90' : 'text-[#C41230]'
            }`}>
              <span>Showing steps for {activeWorkflow.badge}</span>
            </span>
          )}
        </div>

        {/* Step Cards Flex Container with Animated Transition */}
        <div
          key={activeWorkflow.id}
          className="animate-step-fade flex flex-col lg:flex-row lg:items-stretch justify-between gap-4 lg:gap-3"
        >
          {activeWorkflow.steps.map((stepItem, index) => (
            <React.Fragment key={stepItem.step}>
              {/* Step Card */}
              <div
                className={`flex items-center gap-3.5 flex-1 p-4 rounded-xl border transition-all duration-300 ${
                  hoveredAgent
                    ? isDark
                      ? 'bg-red-950/20 border-red-900/40 hover:border-red-800/60 shadow-[0_2px_12px_rgba(0,0,0,0.2)]'
                      : 'bg-[#FFF9FA] border-red-100 hover:border-red-200 shadow-sm'
                    : isDark
                      ? 'bg-slate-900/30 border-slate-800/60 hover:border-slate-700/80'
                      : 'bg-slate-50/50 border-slate-100 hover:border-slate-200'
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-black flex-shrink-0 transition-all duration-300 ${
                    isDark
                      ? 'bg-red-950/60 text-red-400 border border-red-900/50 shadow-[0_2px_8px_rgba(0,0,0,0.3)]'
                      : 'bg-[#FFF1F2] text-[#C41230] border border-red-200/50 shadow-inner'
                  }`}
                >
                  {stepItem.step}
                </div>
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  <h4 className={`font-bold text-sm sm:text-base leading-snug ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                    {stepItem.title}
                  </h4>
                  <p className={`text-xs sm:text-sm font-medium leading-relaxed ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    {stepItem.desc}
                  </p>
                </div>
              </div>

              {/* Arrow Connector between steps (on desktop) */}
              {index < activeWorkflow.steps.length - 1 && (
                <div
                  className={`hidden lg:flex items-center justify-center px-0.5 flex-shrink-0 transition-colors duration-300 ${
                    hoveredAgent
                      ? isDark
                        ? 'text-red-500/70'
                        : 'text-[#C41230]/60'
                      : isDark
                        ? 'text-slate-700'
                        : 'text-slate-300'
                  }`}
                >
                  <svg className="w-6 h-4" fill="none" viewBox="0 0 48 16" stroke="currentColor" strokeWidth="1.75">
                    <path d="M2 8H46M46 8L38 3M46 8L38 13" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  )
}

export default NewVantageAIHomePage